import type { NamespaceKey } from '@giyus/i18n';

export interface SubmenuItem {
  readonly id: string;
  readonly to: string;
  readonly labelKey: NamespaceKey<'shell'>;
  readonly badge?: string;
}

export function buildModuleSubmenus(
  moduleKey: string,
  baseTo: string,
  isNetworkScope: boolean,
): readonly SubmenuItem[] {
  switch (moduleKey) {
    case 'shared-mailboxes':
      return [
        {
          id: 'shared-mailboxes-inbox',
          to: `${baseTo}?tab=inbox`,
          labelKey: 'nav.submenus.shared-mailboxes-inbox',
        },
        {
          id: 'shared-mailboxes-dispatch-bus',
          to: `${baseTo}?tab=dispatchBus`,
          labelKey: 'nav.submenus.shared-mailboxes-dispatch-bus',
        },
        {
          id: 'shared-mailboxes-composition',
          to: `${baseTo}?tab=composition`,
          labelKey: 'nav.submenus.shared-mailboxes-composition',
        },
        {
          id: 'shared-mailboxes-rules',
          to: `${baseTo}?tab=rules`,
          labelKey: 'nav.submenus.shared-mailboxes-rules',
        },
      ];
    case 'studies':
      return isNetworkScope
        ? [
            {
              id: 'studies-mailboxes',
              to: `${baseTo}?tab=mailboxes`,
              labelKey: 'nav.submenus.studies-mailboxes',
            },
            {
              id: 'studies-dossiers',
              to: `${baseTo}?tab=dossiers`,
              labelKey: 'nav.submenus.studies-dossiers',
            },
            {
              id: 'studies-questions',
              to: `${baseTo}?tab=questions`,
              labelKey: 'nav.submenus.studies-questions',
            },
            {
              id: 'studies-catalog',
              to: `${baseTo}?tab=studies`,
              labelKey: 'nav.submenus.studies-catalog',
            },
            {
              id: 'studies-sponsors-cros',
              to: `${baseTo}?tab=sponsors_cros`,
              labelKey: 'nav.submenus.studies-sponsors-cros',
            },
            {
              id: 'studies-rework-billing',
              to: `${baseTo}?tab=rework_billing`,
              labelKey: 'nav.submenus.studies-rework-billing',
            },
            {
              id: 'studies-centers',
              to: `${baseTo}?tab=centers`,
              labelKey: 'nav.submenus.studies-centers',
            },
            {
              id: 'studies-intelligence',
              to: `${baseTo}?tab=intelligence`,
              labelKey: 'nav.submenus.studies-intelligence',
            },
          ]
        : [
            {
              id: 'studies-tenant-catalog',
              to: `${baseTo}?tab=studies`,
              labelKey: 'nav.submenus.studies-tenant-catalog',
            },
            {
              id: 'studies-tenant-dossiers',
              to: `${baseTo}?tab=dossiers`,
              labelKey: 'nav.submenus.studies-tenant-dossiers',
            },
            {
              id: 'studies-tenant-questions',
              to: `${baseTo}?tab=questions`,
              labelKey: 'nav.submenus.studies-tenant-questions',
            },
          ];
    case 'suppliers':
      return isNetworkScope
        ? [
            {
              id: 'suppliers-list',
              to: `${baseTo}?tab=suppliers`,
              labelKey: 'nav.submenus.suppliers-list',
            },
            {
              id: 'suppliers-governance360',
              to: `${baseTo}?tab=governance360`,
              labelKey: 'nav.submenus.suppliers-governance360',
            },
            {
              id: 'suppliers-timeline',
              to: `${baseTo}?tab=timeline`,
              labelKey: 'nav.submenus.suppliers-timeline',
            },
            {
              id: 'suppliers-pendencies',
              to: `${baseTo}?tab=pendencies`,
              labelKey: 'nav.submenus.suppliers-pendencies',
            },
            {
              id: 'suppliers-locality-price-timeline',
              to: `${baseTo}?tab=localityPriceTimeline`,
              labelKey: 'nav.submenus.suppliers-locality-price-timeline',
            },
            {
              id: 'suppliers-inventory',
              to: `${baseTo}?tab=inventory`,
              labelKey: 'nav.submenus.suppliers-inventory',
            },
            {
              id: 'suppliers-quote-comparison',
              to: `${baseTo}?tab=quoteComparison`,
              labelKey: 'nav.submenus.suppliers-quote-comparison',
            },
            {
              id: 'suppliers-pickups',
              to: `${baseTo}?tab=pickups`,
              labelKey: 'nav.submenus.suppliers-pickups',
            },
            {
              id: 'suppliers-financial',
              to: `${baseTo}?tab=financial`,
              labelKey: 'nav.submenus.suppliers-financial',
            },
            {
              id: 'suppliers-coverage',
              to: `${baseTo}?tab=coverage`,
              labelKey: 'nav.submenus.suppliers-coverage',
            },
            {
              id: 'suppliers-os',
              to: `${baseTo}?tab=serviceOrders`,
              labelKey: 'nav.submenus.suppliers-os',
            },
          ]
        : [
            {
              id: 'suppliers-tenant-list',
              to: `${baseTo}?tab=suppliers`,
              labelKey: 'nav.submenus.suppliers-tenant-list',
            },
            {
              id: 'suppliers-tenant-inventory',
              to: `${baseTo}?tab=inventory`,
              labelKey: 'nav.submenus.suppliers-tenant-inventory',
            },
            {
              id: 'suppliers-tenant-pickups',
              to: `${baseTo}?tab=pickups`,
              labelKey: 'nav.submenus.suppliers-tenant-pickups',
            },
            {
              id: 'suppliers-tenant-os',
              to: `${baseTo}?tab=serviceOrders`,
              labelKey: 'nav.submenus.suppliers-tenant-os',
            },
            {
              id: 'suppliers-tenant-timeline',
              to: `${baseTo}?tab=timeline`,
              labelKey: 'nav.submenus.suppliers-tenant-timeline',
            },
          ];
    case 'identity':
      return isNetworkScope
        ? [
            {
              id: 'identity-users',
              to: `${baseTo}/users`,
              labelKey: 'nav.submenus.identity-users',
            },
            {
              id: 'identity-clients',
              to: `${baseTo}/oidc-clients`,
              labelKey: 'nav.submenus.identity-clients',
            },
          ]
        : [
            {
              id: 'identity-tenant-users',
              to: `${baseTo}/users`,
              labelKey: 'nav.submenus.identity-tenant-users',
            },
          ];
    case 'authz':
      return isNetworkScope
        ? [
            {
              id: 'authz-catalog',
              to: `${baseTo}/catalog`,
              labelKey: 'nav.submenus.authz-catalog',
            },
            {
              id: 'authz-matrix',
              to: `${baseTo}/matrix`,
              labelKey: 'nav.submenus.authz-matrix',
            },
            {
              id: 'authz-assignments',
              to: `${baseTo}/assignments`,
              labelKey: 'nav.submenus.authz-assignments',
            },
            {
              id: 'authz-sod',
              to: `${baseTo}/sod-rules`,
              labelKey: 'nav.submenus.authz-sod',
            },
            {
              id: 'authz-breakglass',
              to: `${baseTo}/break-glass`,
              labelKey: 'nav.submenus.authz-breakglass',
            },
            {
              id: 'authz-reviews',
              to: `${baseTo}/access-reviews`,
              labelKey: 'nav.submenus.authz-reviews',
            },
            {
              id: 'authz-pit',
              to: `${baseTo}/point-in-time`,
              labelKey: 'nav.submenus.authz-pit',
            },
            {
              id: 'authz-audit',
              to: `${baseTo}/audit`,
              labelKey: 'nav.submenus.authz-audit',
            },
          ]
        : [
            {
              id: 'authz-tenant-assignments',
              to: `${baseTo}/assignments`,
              labelKey: 'nav.submenus.authz-tenant-assignments',
            },
            {
              id: 'authz-tenant-delegations',
              to: `${baseTo}/delegations`,
              labelKey: 'nav.submenus.authz-tenant-delegations',
            },
            {
              id: 'authz-tenant-breakglass',
              to: `${baseTo}/break-glass`,
              labelKey: 'nav.submenus.authz-breakglass',
            },
            {
              id: 'authz-tenant-audit',
              to: `${baseTo}/audit`,
              labelKey: 'nav.submenus.authz-tenant-audit',
            },
          ];
    case 'people':
      return isNetworkScope
        ? [
            {
              id: 'people-list',
              to: baseTo,
              labelKey: 'nav.submenus.people-list',
            },
            {
              id: 'people-pis',
              to: `${baseTo}/principal-investigators`,
              labelKey: 'nav.submenus.people-pis',
            },
            {
              id: 'people-coordinators',
              to: `${baseTo}/coordinators`,
              labelKey: 'nav.submenus.people-coordinators',
            },
            {
              id: 'people-specialties',
              to: `${baseTo}/specialties`,
              labelKey: 'nav.submenus.settings-specialties-functions',
            },
            {
              id: 'people-research-centers',
              to: `${baseTo}/research-centers`,
              labelKey: 'nav.submenus.studies-centers',
            },
            {
              id: 'people-functions',
              to: `${baseTo}/functions`,
              labelKey: 'nav.submenus.people-functions',
            },
            {
              id: 'people-matrix',
              to: `${baseTo}/matrix`,
              labelKey: 'nav.submenus.people-matrix',
            },
            {
              id: 'people-pit',
              to: `${baseTo}/point-in-time`,
              labelKey: 'nav.submenus.people-pit',
            },
          ]
        : [
            {
              id: 'people-tenant-list',
              to: baseTo,
              labelKey: 'nav.submenus.people-tenant-list',
            },
            {
              id: 'people-tenant-pis',
              to: `${baseTo}/principal-investigators`,
              labelKey: 'nav.submenus.people-tenant-pis',
            },
            {
              id: 'people-tenant-coordinators',
              to: `${baseTo}/coordinators`,
              labelKey: 'nav.submenus.people-tenant-coordinators',
            },
            {
              id: 'people-tenant-matrix',
              to: `${baseTo}/matrix`,
              labelKey: 'nav.submenus.people-tenant-matrix',
            },
          ];
    case 'facilities':
      return isNetworkScope
        ? [
            {
              id: 'facilities-stock-alerts',
              to: `${baseTo}?tab=stock_alerts`,
              labelKey: 'nav.submenus.facilities-stock-alerts',
            },
            {
              id: 'facilities-calibration',
              to: `${baseTo}?tab=calibration`,
              labelKey: 'nav.submenus.facilities-calibration',
            },
            {
              id: 'facilities-timeline-docs',
              to: `${baseTo}?tab=timeline_docs`,
              labelKey: 'nav.submenus.facilities-timeline-docs',
            },
            {
              id: 'facilities-assets',
              to: `${baseTo}?tab=assets`,
              labelKey: 'nav.submenus.facilities-assets',
            },
            {
              id: 'facilities-disposition',
              to: `${baseTo}?tab=disposition`,
              labelKey: 'nav.submenus.facilities-disposition',
            },
            {
              id: 'facilities-suppliers-rbc',
              to: `${baseTo}?tab=suppliers_rbc`,
              labelKey: 'nav.submenus.facilities-suppliers-rbc',
            },
            {
              id: 'facilities-mailbox-reconciliation',
              to: `${baseTo}?tab=mailbox_reconciliation`,
              labelKey: 'nav.submenus.facilities-mailbox-reconciliation',
            },
          ]
        : [
            {
              id: 'facilities-tenant-assets',
              to: `${baseTo}?tab=assets`,
              labelKey: 'nav.submenus.facilities-tenant-assets',
            },
            {
              id: 'facilities-tenant-calibration',
              to: `${baseTo}?tab=calibration`,
              labelKey: 'nav.submenus.facilities-tenant-calibration',
            },
            {
              id: 'facilities-tenant-stock-alerts',
              to: `${baseTo}?tab=stock_alerts`,
              labelKey: 'nav.submenus.facilities-tenant-stock-alerts',
            },
            {
              id: 'facilities-tenant-timeline-docs',
              to: `${baseTo}?tab=timeline_docs`,
              labelKey: 'nav.submenus.facilities-tenant-timeline-docs',
            },
            {
              id: 'facilities-tenant-disposition',
              to: `${baseTo}?tab=disposition`,
              labelKey: 'nav.submenus.facilities-tenant-disposition',
            },
          ];
    case 'documents':
      return [
        {
          id: 'documents-portal-hub',
          to: `${baseTo}?tab=library&domain=ALL`,
          labelKey: 'nav.submenus.documents-portal-hub',
        },
        {
          id: 'documents-domain-global',
          to: `${baseTo}?tab=library&domain=GLOBAL`,
          labelKey: 'nav.submenus.documents-domain-global',
        },
        {
          id: 'documents-domain-center',
          to: `${baseTo}?tab=library&domain=CENTER_ONLY`,
          labelKey: 'nav.submenus.documents-domain-center',
        },
        {
          id: 'documents-domain-restricted',
          to: `${baseTo}?tab=library&domain=RESTRICTED`,
          labelKey: 'nav.submenus.documents-domain-restricted',
        },
      ];
    case 'scheduling':
      return [
        {
          id: 'scheduling-monthly-calendar',
          to: `${baseTo}?tab=monthly_calendar`,
          labelKey: 'nav.submenus.scheduling-monthly-calendar',
        },
        {
          id: 'scheduling-lanes',
          to: `${baseTo}?tab=lanes`,
          labelKey: 'nav.submenus.scheduling-lanes',
        },
        {
          id: 'scheduling-booking-wizard',
          to: `${baseTo}?tab=booking_wizard`,
          labelKey: 'nav.submenus.scheduling-booking-wizard',
        },
        {
          id: 'scheduling-waitlist',
          to: `${baseTo}?tab=waitlist`,
          labelKey: 'nav.submenus.scheduling-waitlist',
        },
      ];
    case 'workflow':
      return isNetworkScope
        ? [
            {
              id: 'wf-tasks',
              to: `${baseTo}?tab=tasks`,
              labelKey: 'nav.submenus.wf-tasks',
            },
            {
              id: 'wf-exceptions',
              to: `${baseTo}?tab=exceptions`,
              labelKey: 'nav.submenus.wf-exceptions',
            },
            {
              id: 'wf-approvals',
              to: `${baseTo}?tab=approvals`,
              labelKey: 'nav.submenus.wf-approvals',
            },
            {
              id: 'wf-ai-flows',
              to: `${baseTo}?tab=ai_flows`,
              labelKey: 'nav.submenus.wf-ai-flows',
            },
            {
              id: 'wf-metrics',
              to: `${baseTo}?tab=metrics`,
              labelKey: 'nav.submenus.wf-metrics',
            },
            {
              id: 'wf-policies',
              to: `${baseTo}?tab=policies`,
              labelKey: 'nav.submenus.wf-policies',
            },
            {
              id: 'wf-rules',
              to: `${baseTo}?tab=rules`,
              labelKey: 'nav.submenus.wf-rules',
            },
          ]
        : [
            {
              id: 'wf-tenant-tasks',
              to: `${baseTo}?tab=tasks`,
              labelKey: 'nav.submenus.wf-tenant-tasks',
            },
            {
              id: 'wf-tenant-exceptions',
              to: `${baseTo}?tab=exceptions`,
              labelKey: 'nav.submenus.wf-tenant-exceptions',
            },
            {
              id: 'wf-tenant-approvals',
              to: `${baseTo}?tab=approvals`,
              labelKey: 'nav.submenus.wf-tenant-approvals',
            },
            {
              id: 'wf-tenant-metrics',
              to: `${baseTo}?tab=metrics`,
              labelKey: 'nav.submenus.wf-tenant-metrics',
            },
          ];
    case 'settings':
      return [
        {
          id: 'settings-specialties-functions',
          to: `${baseTo}?tab=specialties_functions`,
          labelKey: 'nav.submenus.settings-specialties-functions',
        },
        {
          id: 'settings-authorization',
          to: `${baseTo}?tab=authorization`,
          labelKey: 'nav.submenus.settings-authorization',
        },
        {
          id: 'settings-ai-prompts',
          to: `${baseTo}?tab=ai_prompts`,
          labelKey: 'nav.submenus.settings-ai-prompts',
        },
        {
          id: 'settings-integrations',
          to: `${baseTo}?tab=integrations`,
          labelKey: 'nav.submenus.settings-integrations',
        },
        {
          id: 'settings-financial-flags',
          to: `${baseTo}?tab=financial_flags`,
          labelKey: 'nav.submenus.settings-financial-flags',
        },
        {
          id: 'settings-security-audit',
          to: `${baseTo}?tab=security_audit`,
          labelKey: 'nav.submenus.settings-security-audit',
        },
      ];
    default:
      return [];
  }
}
